/**
 * Fallback drill-down for insight/pattern types without a bespoke view. It
 * interprets the payload structurally: a friendly KPI grid from the `metrics`
 * object plus delta-first rows for each `top_*` / `by_*` array, so even
 * un-modelled insights read cleanly instead of dumping raw field names.
 */
import {
  agentBookingsHref,
  carrierBookingsHref,
  customerBookingsHref,
  laneBookingsHref,
  operatorBookingsHref,
  salesPersonBookingsHref,
  stateBookingsHref,
} from "../../feedLinks";
import { formatDimensionValue } from "@/lib/formatDimensionValue";
import {
  compareScale,
  formatMetricValue,
  isFriendlyMetricKey,
  pickMetricLabel,
  rowCompare,
  rowLabel,
  rowSummary,
  type Rec,
  type RowLabelKind,
} from "../formatInsight";
import { explainerFor, periodLabel, sectionTitle } from "../insightCopy";
import {
  ChangeRow,
  EntitySection,
  InsightShell,
  MetricGrid,
  PeriodBanner,
  type MetricItem,
} from "../primitives";

function hrefForRow(kind: RowLabelKind, text: string): string | null {
  switch (kind) {
    case "consignee":
      return customerBookingsHref(text);
    case "sales_person":
      return salesPersonBookingsHref(text);
    case "lane":
      return laneBookingsHref(text);
    case "carrier":
      return carrierBookingsHref(text);
    case "state":
      return stateBookingsHref(text);
    case "agent":
      return agentBookingsHref(text);
    case "opened_by":
      return operatorBookingsHref(text);
    default:
      return null;
  }
}

export function GenericInsightBody({ payload }: { payload: Rec }) {
  const metrics = payload.metrics;
  const metricItems: MetricItem[] =
    metrics && typeof metrics === "object"
      ? Object.entries(metrics as Rec)
          .filter(([k, v]) => typeof v === "number" && isFriendlyMetricKey(k))
          .map(([k, v]) => ({
            label: pickMetricLabel(k),
            value: formatMetricValue(k, v as number),
          }))
      : [];

  const lists = Object.entries(payload).filter(
    ([k, v]) =>
      (k.startsWith("top_") || k.startsWith("by_")) &&
      Array.isArray(v) &&
      v.length > 0 &&
      typeof (v as unknown[])[0] === "object",
  ) as [string, Rec[]][];

  if (metricItems.length === 0 && lists.length === 0) return null;

  const period = periodLabel(payload);

  return (
    <InsightShell explainer={explainerFor(payload)}>
      {period && <PeriodBanner label={period} />}
      {metricItems.length > 0 && <MetricGrid items={metricItems} />}
      {lists.map(([key, rows]) => (
        <EntitySection key={key} title={sectionTitle(key)}>
          {(() => {
            const compareMax = compareScale(
              rows.flatMap((r) => {
                const c = rowCompare(r);
                return c ? [c.prior, c.current] : [];
              }),
            );
            const shareMax = compareScale(rows.map((r) => rowSummary(r).share ?? 0));
            return rows.map((row, i) => {
              const label = rowLabel(row);
              const summary = rowSummary(row);
              const compare = rowCompare(row);
              const party =
                label.kind === "consignee" ||
                label.kind === "sales_person" ||
                label.kind === "agent" ||
                label.kind === "opened_by";
              const priorBookings =
                typeof row.prior_bookings === "number" ? row.prior_bookings : undefined;
              const currentBookings =
                typeof row.current_bookings === "number" ? row.current_bookings : undefined;
              const bookingsCaption =
                priorBookings !== undefined && currentBookings !== undefined
                  ? `${priorBookings} \u2192 ${currentBookings} bookings`
                  : undefined;
              return (
                <ChangeRow
                  key={`${label.text}-${i}`}
                  name={
                    label.kind === "state"
                      ? formatDimensionValue("state", label.text) || label.text
                      : label.text
                  }
                  href={hrefForRow(label.kind, label.text)}
                  party={party}
                  lane={label.kind === "lane"}
                  primary={summary.primary}
                  primaryClassName={summary.primaryClassName}
                  subtitle={compare ? bookingsCaption : summary.subtitle}
                  deltaPct={compare?.deltaPct}
                  bar={
                    compare
                      ? {
                          type: "compare",
                          prior: compare.prior,
                          current: compare.current,
                          unit: compare.unit,
                          max: compareMax,
                        }
                      : summary.ratio
                        ? { type: "ratio", ...summary.ratio }
                        : summary.share !== undefined
                          ? { type: "share", value: summary.share, max: shareMax }
                          : undefined
                  }
                />
              );
            });
          })()}
        </EntitySection>
      ))}
    </InsightShell>
  );
}
